import { observable, decorate, computed } from 'mobx';
import _ from 'lodash';
import { authUserState, uistate } from './common';
import { checkRights, collectCheckBox, formatDate } from '../functions';
import { RightsAccess, NotificationsAccess, DashboardsAccess } from '../shared/AccessModels';

/* --- USER STATE --- */
export class UserState {
	constructor() {
		this.CourierInterface = false;

		this.Filter = authUserState.HasERP && this.CanRecipient
			? { Active : true, TipsRecipient : false } : { Active : true };
		this.Users = [];
		this.IsSendRequest = false;
		this.ActiveUsers = 0;

		this.FilialList = [];
		this.MerchantList = [];
		this.RoleList = [];
		this.NotificationList = [];
		this.RightsList = [];
		this.DashboardList = [];
	}

	get CanManage () { return authUserState.User ? checkRights("ManageUsers") : false; }
	get CanRecipient () { return authUserState.User ? checkRights("ManageRecipient") : false; }

	get UserLimit () { return _.isUndefined(authUserState.CurrentMerchant) ? 0 : authUserState.CurrentMerchant.UserLimit; }
	get HasAvailableUserSlot () { return this.UserLimit === -1 || this.UserLimit > this.ActiveUsers; }

	/** Список предприятий для формы с запретом снятия текущего предприятия */
	get Merchants () {
		return _.map(this.MerchantList, (merchant) => ({
			...merchant,
			Disabled : merchant.Id === authUserState.Merchant
		}));
	}

	/** Формирование столбцов таблицы пользователей с учетом устройства и доступных прав */
	get columns () {
		return {
			...(!uistate.isDevice ? { CreateDate : { filterable : false, width : 90 } } : {}),
			Name : { Header : "Имя" },
			Login : { width : 120 },
			...(!uistate.IsMobile ? { Merchants : {}, Roles : {}, Filials : {} } : {}),
			...(this.CanRecipient ? { Tips : {} } : {}),
			...(this.CanManage ? { Edit : {}, Remove : {} } : {})
		};
	}

	/** Сбор данных с фильтра для отправки на сервер */
	getFilter() { return _.isUndefined(this.Filter.TipsRecipient) ? { Active : this.Filter.Active } : { Active : this.Filter.Active, TipsRecipient : this.Filter.TipsRecipient }; }

	/**
	 * Разбор данных от сервера со списком пользователей
	 * @param {object} data
	 */
	setUsers (data) {
		this.Users = data.Success ? _.map(data.Users, (user) => {
			return _.extend(user, {
				Name : user.FirstName + " " + user.LastName,
				CanRemove : !user.IsAdmin,
				Tips : user.TipsRecipient ? "да" : ""
			});
		}) : [];

		this.ActiveUsers = this.Filter.Active && this.Filter.TipsRecipient !== true
			? _.size(this.Users) : this.ActiveUsers;
		this.IsSendRequest = true;
	}

	/**
	 * Разбор данных от сервера с пользователем
	 * @param {object} user
	 */
	setUser (user) {
		this.User = _.isUndefined(user) ? undefined : new User(user.Id === -1 && !_.isUndefined(authUserState.Merchant)
			? { ...user, MerchantIds : [authUserState.Merchant] } : user, {
				Roles : this.RoleList,
				Notifications : this.NotificationList,
				Rights : this.RightsList,
				Dashboards : this.DashboardList,
				Filials : this.FilialList
			});
	}

}
decorate(UserState, {
	User: observable,
	FilialList: observable,
	MerchantList: observable,
	RoleList: observable,
	NotificationList: observable,
	RightsList: observable,
	DashboardList: observable,

	CourierInterface: observable,

	Users: observable,
	IsSendRequest: observable,
	Filter: observable,

	CanManage: computed,
	CanRecipient: computed,
	UserLimit: computed,
	HasAvailableUserSlot: computed,
	Merchants: computed,
	columns: computed
});

/** ----- КЛАСС ПОЛЬЗОВАТЕЛЯ ----- */
export class User {
	constructor(item, lists = {}) {
		this.Id = item && item.Id ? item.Id : -1;
		this.Active = item && !_.isUndefined(item.Active) ? item.Active : true;
		this.FirstName = item && item.FirstName ? item.FirstName : "";
		this.Patronymic = item && item.Patronymic ? item.Patronymic : "";
		this.LastName = item && item.LastName ? item.LastName : "";
		this.Login = item && item.Login ? item.Login : "";
		this.Password = item && item.Password ? item.Password : "";
		this.Email = item && item.Email ? item.Email : "";
		this.IPList = item && item.IPList ? item.IPList : "";
		this.INN = item && item.INN ? item.INN : "";

		this.TipsRecipient = item && !_.isUndefined(item.TipsRecipient) ? item.TipsRecipient : false;
		this.TipsState = this.TipsRecipient && item && !_.isUndefined(item.TipsState) ? item.TipsState : -1;
		this.TipsActive = item && !_.isUndefined(item.TipsActive) ? item.TipsActive : -1;

		this.IsAdmin = item && !_.isUndefined(item.IsAdmin) ? item.IsAdmin : 0;
		this.IsCourier = item && !_.isUndefined(item.IsCourier) ? item.IsCourier : false;

		this.ShowINNFiscal = item && !_.isUndefined(item.ShowINNFiscal) ? item.ShowINNFiscal : false;

		this.Notifications = new NotificationsAccess(item && item.Notifications, lists.Notifications);
		this.Rights = new RightsAccess(item && item.Rights, _.map(lists.Rights, (right) => {
			return { ...right, Disabled : item && item.IsAdmin > 0 };
		}));
		this.Dashboards = new DashboardsAccess(item && item.Dashboards, lists.Dashboards);

		this.MerchantIds = item && !_.isNil(item.MerchantIds) ? item.MerchantIds : [];
		this.RoleIds = item && !_.isNil(item.Roles) ? item.Roles : [];
		this.RoleList = lists.Roles || [];

		this.Filials = new FilialsAccess(item && item.Id === -1 && !authUserState.HasERP && lists.Filials && lists.Filials.length === 1
			? [lists.Filials[0].Id] : item && item.Filials, item && item.POS, item && item.Cassa, lists.Filials);

		this.ErrorCode = "";
		this.SuccessSave = false;
		this.ButtonLoading = false;
	}

	/** Список активных пользовательских должностей текущего предприятия */
	get Roles () {
		var roles = _.filter(this.RoleList, { MerchantId : authUserState.Merchant, IsCourier : false });

		return _.map(roles, (role) => ({ ...role, Disabled : role.IsDefault }));
	}

	/** Проверка условия, что флаг показа ИНН можно выбрать */
	get canShowInn () {
		var isINN = this.INN.replace(/\D/gi,"").length === 12,
			isMerchant = _.indexOf(this.MerchantIds, authUserState.Merchant) !== -1;

		return isINN && isMerchant;
	}

	/**
	 * Сбор данных с основных полей формы пользователя.
	 * Для должностей и предприятий принимает итоговый массив выбранных ID;
	 * изменение должности дополнительно применяет связанные БПН.
	 * Остальные поля записывает в одноимённые свойства модели.
	 * @param {string} id имя поля
	 * @param {string|boolean|number|array} value новое значение поля
	 */
	collect = (id, value) => {
		if (id === "Roles") {
			const previousRoleIds = this.RoleIds;
			this.RoleIds = value;

			_.each(_.difference(previousRoleIds, value), (roleId) => this.applyRoleAccess(false, roleId));
			_.each(_.difference(value, previousRoleIds), (roleId) => this.applyRoleAccess(true, roleId));
		} else if (id === "MerchantIds") {
			const previousMerchantIds = this.MerchantIds;
			this.MerchantIds = value;

			if (this.IsCourier) {
				_.each(_.difference(previousMerchantIds, value), (merchantId) => this.applyCourierMerchant(merchantId, false));
				_.each(_.difference(value, previousMerchantIds), (merchantId) => this.applyCourierMerchant(merchantId, true));
			}
		} else this[id] = value;
	}

	/**
	 * Назначает или снимает системную курьерскую должность выбранного предприятия
	 * и применяет связанные с ней права, дашборды и уведомления.
	 * @param {number} merchantId ID выбранного предприятия
	 * @param {boolean} added флаг назначения предприятия
	 */
	applyCourierMerchant = (merchantId, added) => {
		var role = _.find(this.RoleList, { MerchantId : merchantId, IsCourier : true });
		if (_.isUndefined(role)) return;

		this.RoleIds = collectCheckBox(role.Id, added, this.RoleIds);
		this.applyRoleAccess(added, role.Id);
	}

	/**
	 * Применяет к пользователю права, дашборды и уведомления изменённой должности.
	 * Итоговые значения рассчитываются по настройкам выбранных должностей и передаются моделям доступа.
	 * @param {boolean} added флаг назначения должности
	 * @param {number} id ID назначенной или снятой должности
	 */
	applyRoleAccess = (added, id) => {
		this.Rights.setChecked(this.getRoleItems(added, id, "Rights"));
		this.Dashboards.setChecked(this.getRoleDashboards(added, id));
		this.Notifications.setChecked(this.getRoleItems(added, id, "Notifications"));
	}

	/**
	 * Рассчитывает итоговый плоский список прав или уведомлений после изменения должности.
	 * Дашборды обрабатываются отдельно в getRoleDashboards, так как разделены по типам.
	 * При назначении добавляет настройки должности, при снятии удаляет только те,
	 * которые больше не предоставляет ни одна из оставшихся выбранных должностей.
	 * @param {boolean} added флаг назначения должности
	 * @param {number} id ID назначенной или снятой должности
	 * @param {"Rights"|"Notifications"} type тип настроек доступа
	 * @returns {[string]|[number]} итоговый список прав или ID уведомлений
	 */
	getRoleItems = (added, id, type) => {
		var availableRoles = this.IsCourier ? _.filter(this.RoleList, { IsCourier : true }) : this.Roles,
			role = _.find(availableRoles, { Id : id }),
			roles = _.filter(availableRoles, (item) => _.indexOf(this.RoleIds, item.Id) !== -1),
			checkedItems = this[type].getData(),
			roleItems = _.compact(_.flatten(_.map(roles, type)));

		if (added) return _.union(checkedItems, role[type]);

		_.each(_.difference(role[type], roleItems), (item) => {
			checkedItems = _.without(checkedItems, item);
		});

		return checkedItems;
	}

	/**
	 * Рассчитывает итоговые рабочие столы по типам после назначения или снятия должности.
	 * При назначении добавляет рабочие столы должности к текущим настройкам пользователя.
	 * При снятии сохраняет рабочие столы, которые предоставляет хотя бы одна оставшаяся должность.
	 * @param {boolean} added флаг назначения должности
	 * @param {number} id ID назначенной или снятой должности
	 * @returns {{ Menu?: [number], Sidebar?: [number], Widget?: [number] }} итоговые рабочие столы по типам
	 */
	getRoleDashboards = (added, id) => {
		var availableRoles = this.IsCourier ? _.filter(this.RoleList, { IsCourier : true }) : this.Roles,
			role = _.find(availableRoles, { Id : id }),
			roles = _.filter(availableRoles, (item) => _.indexOf(this.RoleIds, item.Id) !== -1),
			dashboards = this.Dashboards.getData();

		_.each(["Menu", "Sidebar", "Widget"], (type) => {
			var checkedItems = dashboards[type] || [],
				roleItems = _.uniq(_.compact(_.flatten(_.map(_.map(roles, "Dashboards"), type))));

			if (added) dashboards[type] = _.union(checkedItems, role.Dashboards[type]);
			else dashboards[type] = _.difference(checkedItems, _.difference(role.Dashboards[type], roleItems));
		});

		return dashboards;
	}

	/**
	 * Проверка корректности заполнения данных пользователя.
	 * 1. Телефон должен содержать не менее 11 цифр.
	 * 2. Email должен соответствовать базовому формату адреса.
	 * 3. Заполненный ИНН кассира должен содержать 12 цифр.
	 * 4. У активного пользователя должна быть выбрана хотя бы одна должность.
	 * 5. У активного пользователя должен быть выбран хотя бы один филиал.
	 * @returns {boolean} флаг корректности заполнения
	 */
	validate = () => {
		this.ErrorCode = "";
		this.SuccessSave = false;

		if (this.Login.replace(/\D/gi,"").length < 11) {
			this.ErrorCode = "WRONG_PHONE_LENGTH";
			return false;
		}

		if (!/.+@.+\..+/i.test(this.Email)) {
			this.ErrorCode = "WRONG_USER_EMAIL";
			return false;
		}

		if (this.INN !== "" && this.INN.replace(/\D/gi,"").length !== 12) {
			this.ErrorCode = "WRONG_USER_INN";
			return false;
		}

		if (this.Active && _.isEmpty(this.RoleIds)) {
			this.ErrorCode = "EMPTY_USER_ROLE";
			return false;
		}

		if (this.Active && _.isEmpty(this.Filials.Filials)) {
			this.ErrorCode = "EMPTY_USER_FILIAL";
			return false;
		}

		return true;
	}

	/**
	 * Сбор данных пользователя и связанных моделей доступа в формат API UserSave.
	 * @returns {{
	 *   Id: number,
	 *   Active: boolean,
	 *   FirstName: string,
	 *   Patronymic?: string,
	 *   LastName: string,
	 *   Login: string,
	 *   Password: string,
	 *   Email: string,
	 *   IPList: string,
	 *   INN: string,
	 *   TipsRecipient: boolean,
	 *   TipsState: number,
	 *   TipsActive: number,
	 *   IsAdmin: number,
	 *   IsCourier: boolean,
	 *   ShowINNFiscal: boolean,
	 *   Rights?: string,
	 *   Notifications?: string,
	 *   "Dashboards.Menu"?: string,
	 *   "Dashboards.Sidebar"?: string,
	 *   "Dashboards.Widget"?: string,
	 *   Roles?: string,
	 *   MerchantIds?: string,
	 *   Filials?: string,
	 *   POS?: string,
	 *   Cassa?: string
	 * }} данные пользователя в формате API UserSave
	 */
	getData = () => {
		var data = {
			Id: this.Id,
			Active: this.Active,
			FirstName: this.FirstName,
			LastName: this.LastName,
			Login: this.Login.indexOf("(") !== -1 ? this.Login.replace(/\D/gi,"") : this.Login,
			Password: this.Password,
			Email: this.Email,
			IPList: this.IPList,
			INN: this.INN ? this.INN.replace(/\D/gi,"") : this.INN,
			TipsRecipient: this.TipsRecipient,
			TipsState: this.TipsState,
			TipsActive: this.TipsActive,
			IsAdmin: this.IsAdmin,
			IsCourier: this.IsCourier,
			ShowINNFiscal: this.ShowINNFiscal
		};

		if (this.Patronymic !== "") data.Patronymic = this.Patronymic;

		if (!_.isEmpty(this.Rights.getData())) data.Rights = this.Rights.getData().join(",");
		if (!_.isEmpty(this.Notifications.getData())) data.Notifications = this.Notifications.getData().join(",");
		_.each(this.Dashboards.getData(), (list, type) => {
			data["Dashboards." + type] = list.join(",");
		});

		if (!_.isEmpty(this.RoleIds)) data.Roles = this.RoleIds.join(",");
		if (!_.isEmpty(this.MerchantIds)) data.MerchantIds = this.MerchantIds.join(",");


		_.each(this.Filials.getData(), (list, name) => {
			if (!_.isEmpty(list)) data[name] = list.join(",");
		});



		return data;
	}
}
decorate(User, {
	FirstName: observable,
	Patronymic: observable,
	LastName: observable,
	Login: observable,
	Password: observable,
	Email: observable,
	IPList: observable,
	Notifications: observable,
	Filials: observable,
	INN: observable,
	TipsState: observable,
	TipsActive: observable,
	TipsRecipient: observable,
	ShowINNFiscal: observable,
	MerchantIds: observable,
	RoleIds: observable,
	RoleList: observable,
	ButtonLoading: observable,
	ErrorCode: observable,
	SuccessSave: observable,
	Roles: computed,
	canShowInn: computed
});

/** ----- МОДЕЛЬ ДОСТУПА К ФИЛИАЛАМ ----- */
export class FilialsAccess {
	constructor(filials, pos, cassa, items) {
		this.Filials = !_.isNil(filials) ? filials : [];
		this.POS = !_.isNil(pos) ? pos : [];
		this.Cassa = !_.isNil(cassa) ? cassa : [];
		this.Items = !_.isNil(items) ? items : [];
	}

	/** Готовый список городов с числовым ID, названием и филиалами */
	get Cities() {
		return _.map(_.groupBy(this.Items, "CityId"), (filials) => ({
			Id : filials[0].CityId,
			Title : filials[0].City,
			Filials : _.map(filials, (filial) => ({
				Id : filial.Id,
				Title : filial.Name,
				POS : _.map(filial.POS, (pos) => ({
					Id : pos.Id,
					Title : pos.Name,
					HasCassa : pos.HasCassa
				}))
			}))
		}));
	}

	/** Списки ID точек продаж по ID филиала */
	get FilialPOS() {
		return _.keyBy(_.map(this.Items, (filial) => ({
			Id : filial.Id,
			POS : _.map(filial.POS, "Id")
		})), "Id");
	}

	/** Выбран ли каждый филиал соответствующего города */
	get CheckAll() {
		var checked = {};

		_.each(this.Cities, (city) => {
			checked[city.Id] = _.isEmpty(_.difference(_.map(city.Filials, "Id"), this.Filials));
		});

		return checked;
	}

	/**
	 * Состояние группового checkbox филиала по выбранным POS без учёта касс.
	 * @param {number} filialId ID филиала
	 * @returns {boolean|null} всё выбрано, ничего не выбрано или выбрана часть
	 */
	getPOSCheckedState = (filialId) => {
		var filial = this.FilialPOS[filialId],
			posIds = filial ? filial.POS : [];

		if (_.isEmpty(posIds)) return _.indexOf(this.Filials, filialId) !== -1;

		var checkedCount = _.size(_.intersection(posIds, this.POS));

		if (checkedCount === 0) return false;
		return checkedCount === posIds.length ? true : null;
	}

	/**
	 * Сбор данных с чекбокса «Выбрать все» для филиалов одного города.
	 * При выборе добавляет филиалы города и все их POS, при снятии очищает также связанные кассы.
	 * @param {number} cityId ID города
	 * @param {boolean} value состояние checkbox
	 */
	collectCity = (cityId, value) => {
		var city = _.find(this.Cities, { Id : cityId }),
			filials = city ? city.Filials : [],
			filialIds = _.map(filials, "Id"),
			posIds = _.flatten(_.map(filials, (filial) => _.map(filial.POS, "Id")));

		this.Filials = value ? _.union(this.Filials, filialIds) : _.difference(this.Filials, filialIds);
		this.POS = value ? _.union(this.POS, posIds) : _.difference(this.POS, posIds);
		this.Cassa = value ? this.Cassa : _.difference(this.Cassa, posIds);
	}

	/**
	 * Сбор данных с формы доступа к филиалам, точкам продаж и кассам.
	 * Выбор филиала автоматически выбирает его POS, а снятие филиала очищает связанные POS и кассы.
	 * @param {string} name название параметра
	 * @param {boolean} value состояние checkbox
	 */
	collect = (name, value) => {
		var type = name.split("_")[0],
			id = parseInt(name.split("_")[1], 10);

		if (type === "Filials") {
			var filial = this.FilialPOS[id];
			if (!filial) return;

			if (value) {
				this.Filials = _.union(this.Filials, [id]);
				this.POS = _.union(this.POS, filial.POS);
			} else {
				this.Filials = _.without(this.Filials, id);
				this.POS = _.difference(this.POS, filial.POS);
				this.Cassa = _.difference(this.Cassa, filial.POS);
			}
		} else if (type === "POS") {
			if (value) this.POS = _.union(this.POS, [id]);
			else {
				this.POS = _.without(this.POS, id);
				this.Cassa = _.without(this.Cassa, id);
			}
		} else if (type === "Cassa") {
			this.Cassa = value ? _.union(this.Cassa, [id]) : _.without(this.Cassa, id);
		}
	}

	/**
	 * Сбор выбранных филиалов, точек продаж и касс.
	 * @returns {{ Filials: [number], POS: [number], Cassa: [number] }}
	 */
	getData = () => {
		return {
			Filials : _.clone(this.Filials),
			POS : _.clone(this.POS),
			Cassa : _.clone(this.Cassa)
		};
	}
}
decorate(FilialsAccess, {
	Filials: observable,
	POS: observable,
	Cassa: observable,
	Items: observable,
	Cities: computed,
	FilialPOS: computed,
	CheckAll: computed
});

export class CourierState {
	constructor() {
		this.CourierInterface = true;
		this.Filter = { Active : true };
		this.Users = [];
		this.IsSendRequest = false;
		this.FilialList = [];
		this.RoleList = [];
		this.MerchantList = [];
	}

	get CanManage () { return authUserState.User ? checkRights("ManageCouriers") : false; }
	get CanOpenShift () { return authUserState.User ? checkRights("CanCourierShift") : false; }
	get CanToggleShift () { return this.Filter.Active && authUserState.Shift.Active && this.CanOpenShift; }
	get CanRecipient () { return authUserState.User ? checkRights("ManageRecipient") : false; }
	get CanManageINN () { return authUserState.User ? checkRights("ManageCourierINN") : false; }

	/** Формирование столбцов таблицы курьеров с учетом устройства и доступных прав */
	get columns () {
		return {
			...(!uistate.isDevice ? { CreateDate : { filterable : false, width : 90 } } : {}),
			Name : { Header : "Имя" },
			Login : { width : 120 },
			...(!uistate.IsMobile ? {
				Merchants : {},
				Filials : {},
				...(this.Filter.Active && this.CanOpenShift ? {
					ToggleShift : {
						id : "ToggleShift",
						accessor : "ShiftState",
						sortMethod: (a, b) => {
							const states = ["close", "closeAfterResolve", "open", "another", "unavailable", "none"],
								aSort = _.indexOf(states, a),
								bSort = _.indexOf(states, b);

							if (a === b) return 0;
							return aSort - bSort;
						}
					}
				} : {})
			} : {}),
			...(this.Filter.Active && authUserState.HasERP && this.CanRecipient ? { Tips : {} } : {}),
			...(this.CanManage ? { Edit : {}, Remove : {} } : {})
		};
	}

	/** Сбор данных с фильтра для отправки на сервер */
	getFilter() { return { Active : this.Filter.Active }; }

	/**
	 * Расчет состояния ячейки управления сменой курьера
	 * @param {object} user данные курьера
	 * @returns {"open"|"close"|"unavailable"|"another"|"closeAfterResolve"|"none"}
	 */
	getShiftState(user) {
		if (!this.CanToggleShift) return "none";

		if (!user.ShiftDate) {
			return _.indexOf(user.FilialIds || [], authUserState.Shift.FilialId) !== -1
				? "open" : "unavailable";
		}

		if (user.ShiftFilialId !== authUserState.Shift.FilialId) return "another";

		return user.CloseAfterResolve ? "closeAfterResolve" : "close";
	}

	/**
	 * Разбор данных от сервера со списком пользователей
	 * @param {object} data
	 */
	setUsers (data) {
		this.Users = data.Success ? _.map(data.Users, (user) => {
			return _.extend(user, {
				CanRemove : !user.IsAdmin,
				Tips : user.TipsRecipient ? "да" : "",
				ShiftState : this.getShiftState(user),
				ShiftFilialName : _.get(_.find(this.FilialList, { Id : user.ShiftFilialId }) ||
					_.find(authUserState.Filials, { Id : user.ShiftFilialId }), "Name", "")
			});
		}) : [];

		this.IsSendRequest = true;
	}

	/**
	 * Открытие окна смены курьера из строки таблицы
	 * @param {object} courier информация о курьере
	 */
	toggleCourierShift = (courier) => {
		const filial = _.find(authUserState.Filials, { Id : authUserState.Shift.FilialId });

		if (_.isUndefined(filial)) return;
		this.CourierShift = new CourierShift(courier, filial);
	}

	/** Список предприятий, для которых существует системная курьерская должность */
	get Merchants () {
		return _.filter(this.MerchantList, (merchant) => merchant.Type !== 2 &&
			!_.isUndefined(_.find(this.RoleList, { MerchantId : merchant.Id, IsCourier : true })));
	}

	/**
	 * Разбор данных от сервера с пользователем
	 * @param {object} user
	 */
	setUser (user) {
		this.User = _.isUndefined(user) ? undefined : new User(user.Id === -1 && _.isUndefined(user.IsCourier)
			? { ...user, IsCourier : true } : user, {
			// БПН курьера назначаются только системными должностями, поэтому их справочники форме не нужны.
			Roles : this.RoleList,
			Filials : this.FilialList
		});

		if (!_.isUndefined(this.User) && this.User.Id === -1 && this.Merchants.length === 1) {
			// TODO: заменить скрытое использование collect при инициализации на явный метод
			// автоматического назначения единственного предприятия новому курьеру.
			this.User.collect("MerchantIds", [this.Merchants[0].Id]);
		}
	}

}
decorate(CourierState, {
	User: observable,
	FilialList: observable,
	RoleList: observable,
	MerchantList: observable,

	CourierInterface: observable,

	Users: observable,
	IsSendRequest: observable,
	Filter: observable,

	CourierShift: observable,

	CanManage: computed,
	CanOpenShift: computed,
	CanToggleShift: computed,
	CanRecipient: computed,
	CanManageINN: computed,
	Merchants: computed,
	columns: computed
});

/** Модель открытия и закрытия смены курьера */
export class CourierShift {
	/**
	 * @param {object} courier курьер из строки списка
	 * @param {object} filial филиал открытой смены авторизованного пользователя
	 */
	constructor(courier, filial) {
		this.CourierId = courier.Id;
		this.CourierName = courier.Name;
		// Active — требуемое состояние смены для API: true — открыть, false — закрыть.
		this.Active = !courier.ShiftDate;
		this.FilialId = filial.Id;
		this.FilialName = filial.Name;
		this.Date = formatDate("today", "rtime");
		this.CloseAfterResolve = !_.isUndefined(courier.CloseAfterResolve)
			? courier.CloseAfterResolve : false;
		this.ErrorCode = "";
		this.ButtonLoading = false;
	}

	/**
	 * Сбор данных с формы открытия/закрытия курьерской смены
	 * @param {string} id название параметра
	 * @param {string|boolean} value значение параметра
	 */
	collect = (id, value) => { this[id] = value; }

	/**
	 * Данные для запроса открытия/закрытия курьерской смены.
	 * Active: true открывает смену, false закрывает.
	 * @returns {{ CourierId: number, Active: boolean, FilialId: number, Date: string, CloseAfterResolve: boolean }}
	 */
	getData = () => {
		return {
			CourierId : this.CourierId,
			Active : this.Active,
			FilialId : this.FilialId,
			Date : this.Date,
			CloseAfterResolve : this.CloseAfterResolve
		};
	}
}
decorate(CourierShift, {
	Date: observable,
	CloseAfterResolve: observable,
	ErrorCode: observable,
	ButtonLoading: observable
});


export const userState = new UserState();
